import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId, loadSeasons as loadSeasonsForFilter } from "./seasonFilter";
import { LOGO_B64, buildPrintHTML as buildBillSalesReport, printMultipleSalesReports } from "./SalesReports";
import { buildInvoicePrintHTML, printMultipleInvoices } from "./InvoiceRegistry";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import { loadFromSupabase } from "../supabaseDB";

/* Settlements / Liquidaciones — read-only ledger of completed transactions.
 *
 * Two side-by-side panels:
 *  - LEFT: Shipper Settlements (bills with status="paid" AND balance=0)
 *  - RIGHT: Client Settlements (invoices with status="paid" AND balance=0)
 *
 * Both panels share three summary cards at the top: Total Collected
 * (from invoices), Total Paid to Shippers (from bills), Real Profit
 * (collected − paid). Cards recompute from each panel's currently
 * filtered set.
 *
 * Clicking any row opens a read-only DraggableModal showing the full
 * bill/invoice detail plus cross-links: bill detail lists invoices that
 * share its lot; invoice detail lists shipments/bills that share its
 * lot. Links open the related record in the same modal. */

var T = {
  en: {
    title: "Settlements", subtitle: "Completed transactions — read-only ledger",
    shipperPanel: "Shipper Settlements", clientPanel: "Client Settlements",
    totalCollected: "Total Collected", totalPaidShippers: "Total Paid to Shippers", realProfit: "Real Profit",
    lot: "Lot", date: "Date", invoiceProforma: "Invoice / Proforma", shipper: "Shipper", purchaseAmount: "Purchase Amount", totalDeductions: "Total Deductions", amountPaid: "Amount Paid", season: "Season",
    invoiceNum: "Invoice #", customerPO: "Customer PO", client: "Client", salesAmount: "Sales Amount", amountCollected: "Amount Collected",
    selected: "selected", search: "Search...", allShippers: "All shippers", allClients: "All clients", allSeasons: "All seasons",
    salesReportPdf: "Sales Report PDF", invoicePdf: "Invoice PDF",
    printSalesReport: "Print Sales Report", printInvoice: "Print Invoice", close: "Close",
    relatedInvoices: "Related Invoices", relatedShipments: "Related Shipments / Bills",
    noResults: "No results", noRelated: "No related records",
    popupBlocked: "Pop-up blocked. Allow pop-ups for this site.",
  },
  es: {
    title: "Liquidaciones", subtitle: "Transacciones completadas — solo lectura",
    shipperPanel: "Liquidaciones de Exportadores", clientPanel: "Liquidaciones de Clientes",
    totalCollected: "Total Cobrado", totalPaidShippers: "Total Pagado a Exportadores", realProfit: "Ganancia Real",
    lot: "Lote", date: "Fecha", invoiceProforma: "Factura / Proforma", shipper: "Exportador", purchaseAmount: "Monto de Compra", totalDeductions: "Total Deducciones", amountPaid: "Pagado", season: "Temporada",
    invoiceNum: "Factura #", customerPO: "PO Cliente", client: "Cliente", salesAmount: "Monto de Venta", amountCollected: "Cobrado",
    selected: "seleccionados", search: "Buscar...", allShippers: "Todos los exportadores", allClients: "Todos", allSeasons: "Todas",
    salesReportPdf: "Sales Report PDF", invoicePdf: "Factura PDF",
    printSalesReport: "Imprimir Sales Report", printInvoice: "Imprimir Factura", close: "Cerrar",
    relatedInvoices: "Facturas Relacionadas", relatedShipments: "Embarques / Bills Relacionados",
    noResults: "Sin resultados", noRelated: "Sin registros relacionados",
    popupBlocked: "El navegador bloqueó la ventana. Habilita pop-ups.",
  },
};

/* ───────────────── helpers ───────────────── */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function fmtDate(d) { if (!d) return "—"; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

/* Full presentation/pack/label for a line — mirrors the helper in
   BillRegistry. Falls back to a catalog lookup by productId so old
   imported lines (without productDescription persisted) still get a
   real description in the export. Cached at module scope per render. */
var _settleProdsCache = null;
function resolveLineDesc(line) {
  if (!line) return "";
  if (line.productDescription) return line.productDescription;
  if (!line.productId) return "";
  if (!_settleProdsCache) _settleProdsCache = loadLS("gfi-products", []);
  var prod = _settleProdsCache.find(function (p) { return String(p.id) === String(line.productId); });
  if (!prod) return "";
  var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
  return (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
}

/* Paid amount with historical-import fallback — same shape as the
   matching helper in BillRegistry/InvoiceRegistry. */
function paidAmount(rec) {
  var sum = ((rec && rec.payments) || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  if (sum > 0) return sum;
  return parseFloat(rec && rec.amountPaid) || 0;
}

function calcBillSubtotal(bill) {
  var gross = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    gross += (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
  });
  var tFees = 0;
  (bill.fees || []).forEach(function (f) { tFees += parseFloat(f.amount) || 0; });
  var commAmt = gross * ((parseFloat(bill.commissionRate) || 0) / 100);
  return { gross: gross, fees: tFees, commission: commAmt, subtotal: gross - commAmt - tFees };
}
function calcInvoiceTotal(inv) {
  /* Per-line commission with invoice-level fallback, plus fees and
     credits. Mirrors InvoicePreviewModal in BillRegistry and the
     shape InvoiceRegistry uses. TR invoices carry a ~10% commission
     which the previous impl ignored — that's why only zero-commission
     FS invoices ever showed as settled. */
  var invDefaultRate = parseFloat(inv.commissionRate) || 0;
  var gross = 0, commission = 0;
  (inv.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? invDefaultRate : (parseFloat(l.commissionRate) || 0);
    gross += amt;
    commission += amt * (rate / 100);
  });
  var tFees = 0;
  (inv.fees || []).forEach(function (fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
  var tCredits = 0;
  (inv.credits || []).forEach(function (cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
  return { gross: gross, fees: tFees, commission: commission, credits: tCredits, total: gross - commission - tFees + tCredits };
}

function seasonNameFor(seasons, dateStr, sid) {
  if (sid) {
    var byId = seasons.find(function (s) { return String(s.id) === String(sid); });
    if (byId) return byId.seasonName || byId.startDate.substring(0, 4) + "-" + byId.endEstimate.substring(2, 4);
  }
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  var s = seasons.find(function (s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
  return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
}

/* A bill/invoice is "settled" when what we've collected matches the
   computed total. Previously this also required status === "paid",
   but bugs in the payment sync flow leave status desynced ("sent" or
   "finalized" instead of "paid") even when the record is fully paid.
   The math (paid ≈ total) is the source of truth. `> 0.01` guard
   skips empty/draft records with total = 0. */
function isBillSettled(bill) {
  var sub = calcBillSubtotal(bill).subtotal;
  var paid = paidAmount(bill);
  return sub > 0.01 && Math.abs(sub - paid) <= 0.01;
}
function isInvoiceSettled(inv) {
  var tot = calcInvoiceTotal(inv).total;
  var paid = paidAmount(inv);
  return tot > 0.01 && Math.abs(tot - paid) <= 0.01;
}

/* ───────────────── bill detail (read-only) ───────────────── */
function BillDetailReadOnly({ bill, t, lang, onClose, onOpenInvoice }) {
  var totals = calcBillSubtotal(bill);
  var paid = paidAmount(bill);
  var due = +(totals.subtotal - paid).toFixed(2);

  /* Related invoices: any invoice whose lot matches this bill's lot. */
  var relatedInvoices = useMemo(function () {
    var all = loadLS("gfi-invoices", []);
    return all.filter(function (inv) { return String(inv.lot) === String(bill.lot); });
  }, [bill.lot]);

  var handlePrint = function () {
    var suppliers = loadLS("gfi-suppliers", []);
    var html = buildBillSalesReport(bill, suppliers);
    var w = window.open("", "_blank"); if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600);
  };

  var fmtMoneyNoSign = function (n) { var v = parseFloat(n) || 0; return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

  return (
    <DraggableModal title={"LOT " + (bill.lot || "—") + " · " + (bill.shipperName || "")} onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}>
      <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "var(--color-accent)" }}>LOT {bill.lot}</div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>{bill.shipperName} · {bill.proforma || "—"}</div>
        </div>
        <button onClick={handlePrint} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{"🖨 "}{t.printSalesReport}</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}>
        {/* Info grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", marginBottom: 18, fontSize: 12 }}>
          {[["Lot", bill.lot, true], ["Bill Date", fmtDate(bill.billDate)], ["Due Date", fmtDate(bill.dueDate)], ["Shipper", bill.shipperName], ["Proforma", bill.proforma, true], ["Shipper Invoice", bill.shipperInvoice, true], ["Entry #", bill.entryNumber, true], ["Commission", (bill.commissionRate || 0) + "%"]].map(function (arr, i) {
            return <div key={i}><span style={{ color: "var(--color-text-muted)", fontSize: 10 }}>{arr[0]}</span><div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
          })}
        </div>

        {/* Product lines */}
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>Products</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
          <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
            <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Product</th>
            <th style={{ padding: "6px 4px", textAlign: "center", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Cat</th>
            <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Cases</th>
            <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Reported Price</th>
            <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Amount</th>
            <th style={{ padding: "6px 4px", textAlign: "center", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Status</th>
          </tr></thead>
          <tbody>{(bill.lines || []).map(function (l, i) {
            var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
            return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
              <td style={{ padding: "7px 6px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}</td>
              <td style={{ padding: "7px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-muted)" }}>{l.category || ""}</td>
              <td style={{ padding: "7px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{l.cases}</td>
              <td style={{ padding: "7px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(l.reportedPrice)}</td>
              <td style={{ padding: "7px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(amt)}</td>
              <td style={{ padding: "7px 4px", textAlign: "center", fontSize: 10, color: "var(--color-text-muted)" }}>{l.saleStatus || "—"}</td>
            </tr>;
          })}</tbody>
        </table>

        {/* Fees */}
        {(bill.fees || []).length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>Fees</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
              <tbody>{(bill.fees || []).map(function (f, i) {
                return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "6px 6px", fontWeight: 500 }}>{f.feeType === "__custom__" ? f.feeCustom : f.feeType}</td>
                  <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-muted)" }}>{f.qty > 1 ? f.qty + " × " + fmtMoney(f.rate) : ""}</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "#DC2626" }}>({fmtMoney(f.amount)})</td>
                </tr>;
              })}</tbody>
            </table>
          </>
        )}

        {/* Summary */}
        <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, fontSize: 12, marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Gross Sales</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>${fmtMoneyNoSign(totals.gross)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>Commission ({bill.commissionRate || 0}%)</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>(${fmtMoneyNoSign(totals.commission)})</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>Total Fees</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>(${fmtMoneyNoSign(totals.fees)})</span></div>
          <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700 }}><span>Subtotal</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>${fmtMoneyNoSign(totals.subtotal)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}><span>Less: Paid</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>(${fmtMoneyNoSign(paid)})</span></div>
          <div style={{ borderTop: "2px solid var(--color-accent)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14, color: "#059669" }}><span>Balance</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>${fmtMoneyNoSign(due)}</span></div>
        </div>

        {/* Related invoices */}
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.relatedInvoices}</div>
        {relatedInvoices.length === 0 ? (
          <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noRelated}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 14 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Invoice #</th>
              <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Date</th>
              <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Client</th>
              <th style={{ padding: "6px 6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Total</th>
            </tr></thead>
            <tbody>{relatedInvoices.map(function (inv) {
              var tot = calcInvoiceTotal(inv).total;
              return (
                <tr key={inv.id}
                    onClick={function () { onOpenInvoice(inv); }}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={function (e) { e.currentTarget.style.background = "rgba(198,93,0,0.06)"; }}
                    onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>PU# {inv.puNumber} →</td>
                  <td style={{ padding: "8px 6px", color: "var(--color-text-secondary)" }}>{fmtDate(inv.date)}</td>
                  <td style={{ padding: "8px 6px" }}>{inv.clientName || "—"}</td>
                  <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{fmtMoney(tot)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>
    </DraggableModal>
  );
}

/* ───────────────── invoice detail (read-only) ───────────────── */
function InvoiceDetailReadOnly({ invoice, t, lang, onClose, onOpenBill }) {
  var totals = calcInvoiceTotal(invoice);
  var paid = paidAmount(invoice);
  var due = +(totals.total - paid).toFixed(2);

  /* Related bills/shipments: bills whose lot matches the invoice's lot. */
  var relatedBills = useMemo(function () {
    var all = loadLS("gfi-bills", []);
    return all.filter(function (b) { return String(b.lot) === String(invoice.lot); });
  }, [invoice.lot]);

  var handlePrint = function () {
    var clients = loadLS("gfi-clients", []);
    var client = clients.find(function (c) { return String(c.id) === String(invoice.clientId); }) || null;
    var html = buildInvoicePrintHTML(invoice, client);
    var w = window.open("", "_blank"); if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600);
  };

  var fmtMoneyNoSign = function (n) { var v = parseFloat(n) || 0; return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

  return (
    <DraggableModal title={"PU# " + (invoice.puNumber || "—") + " · " + (invoice.clientName || "")} onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}>
      <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#c65d00" }}>PU# {invoice.puNumber}</div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>{invoice.clientName} · LOT {invoice.lot}</div>
        </div>
        <button onClick={handlePrint} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{"🖨 "}{t.printInvoice}</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}>
        {/* Info grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", marginBottom: 18, fontSize: 12 }}>
          {[["Invoice #", invoice.puNumber, true], ["Date", fmtDate(invoice.date)], ["Due Date", fmtDate(invoice.dueDate)], ["Client", invoice.clientName], ["Client Ref", invoice.clientRef, true], ["Lot", invoice.lot, true]].map(function (arr, i) {
            return <div key={i}><span style={{ color: "var(--color-text-muted)", fontSize: 10 }}>{arr[0]}</span><div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
          })}
        </div>

        {/* Product lines */}
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c65d00", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>Products</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
          <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
            <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Cases</th>
            <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Product</th>
            <th style={{ padding: "6px 6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Sale Price</th>
            <th style={{ padding: "6px 6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>Amount</th>
          </tr></thead>
          <tbody>{(invoice.lines || []).map(function (l, i) {
            var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
            return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
              <td style={{ padding: "7px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases}</td>
              <td style={{ padding: "7px 6px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}<div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{l.productDescription || ""}</div></td>
              <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(l.salePrice)}</td>
              <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "#c65d00" }}>{fmtMoney(amt)}</td>
            </tr>;
          })}</tbody>
        </table>

        {/* Deductions */}
        {(invoice.fees || []).filter(function (f) { return f && (f.feeType || f.feeCustom || parseFloat(f.amount) > 0); }).length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c65d00", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>Deductions</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
              <tbody>{(invoice.fees || []).map(function (f, i) {
                if (!f || (!f.feeType && !f.feeCustom && !(parseFloat(f.amount) > 0))) return null;
                var fname = f.feeType === "__custom__" ? f.feeCustom : f.feeType;
                return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "6px 6px", fontWeight: 500 }}>{fname || "Fee"}</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "#DC2626" }}>({fmtMoney(f.amount)})</td>
                </tr>;
              })}</tbody>
            </table>
          </>
        )}

        {/* Summary */}
        <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, fontSize: 12, marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Gross</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>${fmtMoneyNoSign(totals.gross)}</span></div>
          {totals.fees > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>Deductions</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>(${fmtMoneyNoSign(totals.fees)})</span></div>}
          <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700 }}><span>Total</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>${fmtMoneyNoSign(totals.total)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}><span>Less: Collected</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>(${fmtMoneyNoSign(paid)})</span></div>
          <div style={{ borderTop: "2px solid #c65d00", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14, color: "#059669" }}><span>Balance</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>${fmtMoneyNoSign(due)}</span></div>
        </div>

        {/* Related bills */}
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c65d00", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.relatedShipments}</div>
        {relatedBills.length === 0 ? (
          <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noRelated}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 14 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Lot</th>
              <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Bill Date</th>
              <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Shipper</th>
              <th style={{ padding: "6px 6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Subtotal</th>
            </tr></thead>
            <tbody>{relatedBills.map(function (b) {
              var sub = calcBillSubtotal(b).subtotal;
              return (
                <tr key={b.id}
                    onClick={function () { onOpenBill(b); }}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={function (e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                    onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>LOT {b.lot} →</td>
                  <td style={{ padding: "8px 6px", color: "var(--color-text-secondary)" }}>{fmtDate(b.billDate)}</td>
                  <td style={{ padding: "8px 6px" }}>{b.shipperName || "—"}</td>
                  <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{fmtMoney(sub)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>
    </DraggableModal>
  );
}

/* ───────────────── main component ───────────────── */
export default function Settlements({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang];
  var [bills, setBills] = useState([]);
  var [invoices, setInvoices] = useState([]);
  var [seasons, setSeasons] = useState([]);

  useEffect(function () {
    /* Source of truth = Supabase. Other modules dual-write to localStorage
       so cross-entity reads (suppliers, clients) still come from cache. */
    var alive = true;
    Promise.all([
      loadFromSupabase("bills").catch(function () { return loadLS("gfi-bills", []); }),
      loadFromSupabase("invoices").catch(function () { return loadLS("gfi-invoices", []); }),
      loadFromSupabase("seasons").catch(function () { return loadLS("gfi-seasons", []); }),
    ]).then(function (vals) {
      if (!alive) return;
      setBills(vals[0] || []);
      setInvoices(vals[1] || []);
      setSeasons(vals[2] || []);
    });
    return function () { alive = false; };
  }, []);

  /* Per-panel filters and selection. */
  var [billSearch, setBillSearch] = useState("");
  var [billShipper, setBillShipper] = useState("");
  /* Season filter — multi-select grouped per CLAUDE.md "REGLA DE FILTRO
     DE SEASON". Pre-selects all Open seasons on mount. Both panels carry
     independent filter state so toggling on one side never affects the
     other. */
  var [billSeasons, setBillSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [billDateFrom, setBillDateFrom] = useState(""); var [billDateTo, setBillDateTo] = useState("");
  var [billSelected, setBillSelected] = useState({});
  var [billSort, setBillSort] = useState({ col: "billDate", dir: "desc" });

  var [invSearch, setInvSearch] = useState("");
  var [invClient, setInvClient] = useState("");
  var [invSeasons, setInvSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [invDateFrom, setInvDateFrom] = useState(""); var [invDateTo, setInvDateTo] = useState("");
  var [invSelected, setInvSelected] = useState({});
  var [invSort, setInvSort] = useState({ col: "date", dir: "desc" });

  var [viewingBill, setViewingBill] = useState(null);
  var [viewingInvoice, setViewingInvoice] = useState(null);
  /* Inline expand state per panel — keyed by record id. Separate from row
     click (which still opens the full detail modal). Expanding a row also
     highlights matching rows (by lot) in the opposite panel for fast
     visual cross-traceability. */
  var [expandedBills, setExpandedBills] = useState({});
  var [expandedInvoices, setExpandedInvoices] = useState({});
  var billRowRefs = useRef({});
  var invRowRefs = useRef({});
  var [toast, setToast] = useState("");

  /* Settled-only data sets — historic + current paid records with zero balance. */
  var settledBills = useMemo(function () {
    return bills
      .filter(isBillSettled)
      .map(function (b) {
        var totals = calcBillSubtotal(b);
        var paid = paidAmount(b);
        /* Combined "Invoice / Proforma" string for the table column. Either
           field may be empty; show only what's present, joined with " / ". */
        var invProfParts = [b.shipperInvoice, b.proforma].filter(Boolean);
        return {
          ...b,
          _gross: totals.gross,
          _deductions: totals.commission + totals.fees,
          _paid: paid,
          _invProf: invProfParts.join(" / "),
          _season: seasonNameFor(seasons, b.billDate, b.seasonId),
          _seasonId: resolveSeasonId(seasons, b.billDate, b.seasonId),
        };
      });
  }, [bills, seasons]);
  var settledInvoices = useMemo(function () {
    return invoices
      .filter(isInvoiceSettled)
      .map(function (inv) {
        var t = calcInvoiceTotal(inv);
        var paid = paidAmount(inv);
        /* Sales Amount = gross (cases × salePrice) so the math reads
           Sales − Deductions = Collected, parallel to the bill side. */
        return {
          ...inv,
          _amount: t.gross,
          _deductions: t.fees + t.commission - t.credits,
          _paid: paid,
          _customerPO: inv.clientRef || "",
          _season: seasonNameFor(seasons, inv.date, inv.seasonId),
          _seasonId: resolveSeasonId(seasons, inv.date, inv.seasonId),
        };
      });
  }, [invoices, seasons]);

  var uniqueShippers = useMemo(function () { return [...new Set(settledBills.map(function (b) { return b.shipperName; }).filter(Boolean))].sort(); }, [settledBills]);
  var uniqueClients = useMemo(function () { return [...new Set(settledInvoices.map(function (i) { return i.clientName; }).filter(Boolean))].sort(); }, [settledInvoices]);
  var uniqueSeasons = useMemo(function () { return [...new Set(seasons.map(function (s) { return s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4); }).filter(Boolean))].sort(); }, [seasons]);

  var filteredBills = useMemo(function () {
    var list = settledBills;
    if (billSearch) { var s = billSearch.toLowerCase(); list = list.filter(function (b) { return String(b.lot || "").includes(s) || (b.shipperName || "").toLowerCase().includes(s); }); }
    if (billShipper) list = list.filter(function (b) { return b.shipperName === billShipper; });
    if (billSeasons.length > 0) list = list.filter(function (b) { return billSeasons.indexOf(b._seasonId) >= 0; });
    if (billDateFrom || billDateTo) list = list.filter(function (b) { var d = b.billDate || ""; if (!d) return false; if (billDateFrom && d < billDateFrom) return false; if (billDateTo && d > billDateTo) return false; return true; });
    return list;
  }, [settledBills, billSearch, billShipper, billSeasons, billDateFrom, billDateTo]);

  var filteredInvoices = useMemo(function () {
    var list = settledInvoices;
    if (invSearch) { var s = invSearch.toLowerCase(); list = list.filter(function (i) { return String(i.puNumber || "").includes(s) || (i.clientName || "").toLowerCase().includes(s); }); }
    if (invClient) list = list.filter(function (i) { return i.clientName === invClient; });
    if (invSeasons.length > 0) list = list.filter(function (i) { return invSeasons.indexOf(i._seasonId) >= 0; });
    if (invDateFrom || invDateTo) list = list.filter(function (i) { var d = i.date || ""; if (!d) return false; if (invDateFrom && d < invDateFrom) return false; if (invDateTo && d > invDateTo) return false; return true; });
    return list;
  }, [settledInvoices, invSearch, invClient, invSeasons, invDateFrom, invDateTo]);

  /* Sets of lots being expanded in each panel. Used to highlight matching
     rows in the OPPOSITE panel. Invoices may have lot stored at line
     level rather than the top-level field, so we collect both. */
  var highlightedInvoiceLots = useMemo(function () {
    var s = new Set();
    settledBills.forEach(function (b) {
      if (!expandedBills[b.id]) return;
      if (b.lot) s.add(String(b.lot));
    });
    return s;
  }, [expandedBills, settledBills]);
  var highlightedBillLots = useMemo(function () {
    var s = new Set();
    settledInvoices.forEach(function (inv) {
      if (!expandedInvoices[inv.id]) return;
      if (inv.lot) s.add(String(inv.lot));
      (inv.lines || []).forEach(function (l) { if (l.lot) s.add(String(l.lot)); });
    });
    return s;
  }, [expandedInvoices, settledInvoices]);
  /* Reverse: an invoice should be considered "matching" if any of its
     lots (top-level or any line) overlaps with the highlighted set. */
  function invoiceMatchesHighlight(inv) {
    if (highlightedInvoiceLots.size === 0) return false;
    if (inv.lot && highlightedInvoiceLots.has(String(inv.lot))) return true;
    var lines = inv.lines || [];
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].lot && highlightedInvoiceLots.has(String(lines[i].lot))) return true;
    }
    return false;
  }

  /* When the set of highlighted invoice lots grows, scroll the right panel
     to the first matching invoice row. Same for the left panel when invoice
     expansion changes. The scroll container is the row's parent .overflow
     div — scrollIntoView with block:"nearest" stays within its scroller. */
  useEffect(function () {
    if (highlightedInvoiceLots.size === 0) return;
    /* Find first highlighted invoice row in DOM order — walk sortedInvoices
       so we get the first VISIBLE match, not just any ref. */
    for (var i = 0; i < sortedInvoices.length; i++) {
      var inv = sortedInvoices[i];
      if (invoiceMatchesHighlight(inv)) {
        var el = invRowRefs.current[inv.id];
        if (el) { try { el.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) {} }
        break;
      }
    }
  }, [highlightedInvoiceLots, sortedInvoices]);
  useEffect(function () {
    if (highlightedBillLots.size === 0) return;
    for (var i = 0; i < sortedBills.length; i++) {
      var b = sortedBills[i];
      if (b.lot && highlightedBillLots.has(String(b.lot))) {
        var el = billRowRefs.current[b.id];
        if (el) { try { el.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) {} }
        break;
      }
    }
  }, [highlightedBillLots, sortedBills]);

  var sortedBills = useMemo(function () {
    var arr = [...filteredBills];
    arr.sort(function (a, b) {
      var va, vb;
      switch (billSort.col) {
        case "lot": va = parseInt(a.lot, 10) || 0; vb = parseInt(b.lot, 10) || 0; return billSort.dir === "asc" ? va - vb : vb - va;
        case "billDate": va = a.billDate || ""; vb = b.billDate || ""; break;
        case "_invProf": va = a._invProf || ""; vb = b._invProf || ""; break;
        case "shipperName": va = a.shipperName || ""; vb = b.shipperName || ""; break;
        case "_gross": va = a._gross; vb = b._gross; return billSort.dir === "asc" ? va - vb : vb - va;
        case "_deductions": va = a._deductions; vb = b._deductions; return billSort.dir === "asc" ? va - vb : vb - va;
        case "_paid": va = a._paid; vb = b._paid; return billSort.dir === "asc" ? va - vb : vb - va;
        case "_season": va = a._season || ""; vb = b._season || ""; break;
        default: va = a[billSort.col] || ""; vb = b[billSort.col] || "";
      }
      if (typeof va === "string") return billSort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return billSort.dir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [filteredBills, billSort]);

  var sortedInvoices = useMemo(function () {
    var arr = [...filteredInvoices];
    arr.sort(function (a, b) {
      var va, vb;
      switch (invSort.col) {
        case "puNumber": va = parseInt(a.puNumber, 10) || 0; vb = parseInt(b.puNumber, 10) || 0; return invSort.dir === "asc" ? va - vb : vb - va;
        case "date": va = a.date || ""; vb = b.date || ""; break;
        case "_customerPO": va = a._customerPO || ""; vb = b._customerPO || ""; break;
        case "clientName": va = a.clientName || ""; vb = b.clientName || ""; break;
        case "_amount": va = a._amount; vb = b._amount; return invSort.dir === "asc" ? va - vb : vb - va;
        case "_deductions": va = a._deductions; vb = b._deductions; return invSort.dir === "asc" ? va - vb : vb - va;
        case "_paid": va = a._paid; vb = b._paid; return invSort.dir === "asc" ? va - vb : vb - va;
        case "_season": va = a._season || ""; vb = b._season || ""; break;
        default: va = a[invSort.col] || ""; vb = b[invSort.col] || "";
      }
      if (typeof va === "string") return invSort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return invSort.dir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [filteredInvoices, invSort]);

  /* Summary cards aggregate from the currently filtered sets in each panel.
     Real Profit = total collected (invoices) − total paid to shippers (bills). */
  var totalCollected = useMemo(function () { return filteredInvoices.reduce(function (s, i) { return s + i._paid; }, 0); }, [filteredInvoices]);
  var totalPaidShippers = useMemo(function () { return filteredBills.reduce(function (s, b) { return s + b._paid; }, 0); }, [filteredBills]);
  var realProfit = totalCollected - totalPaidShippers;

  /* Multi-print using existing PDF builders. */
  var printSelectedBills = function () {
    var sel = sortedBills.filter(function (b) { return billSelected[b.id]; });
    if (sel.length === 0) return;
    printMultipleSalesReports(sel, loadLS("gfi-suppliers", []), function (err) {
      if (err === "popup-blocked") setToast(t.popupBlocked);
    });
  };
  var printSelectedInvoices = function () {
    var sel = sortedInvoices.filter(function (i) { return invSelected[i.id]; });
    if (sel.length === 0) return;
    printMultipleInvoices(sel, loadLS("gfi-clients", []), function (err) {
      if (err === "popup-blocked") setToast(t.popupBlocked);
    });
  };

  var billSelectCount = Object.keys(billSelected).length;
  var invSelectCount = Object.keys(invSelected).length;
  var billSelectTotal = useMemo(function () { return sortedBills.filter(function (b) { return billSelected[b.id]; }).reduce(function (s, b) { return s + b._paid; }, 0); }, [sortedBills, billSelected]);
  var invSelectTotal = useMemo(function () { return sortedInvoices.filter(function (i) { return invSelected[i.id]; }).reduce(function (s, i) { return s + i._paid; }, 0); }, [sortedInvoices, invSelected]);

  var toggleBillSort = function (col) { setBillSort(function (p) { return p.col === col ? { col: col, dir: p.dir === "asc" ? "desc" : "asc" } : { col: col, dir: "desc" }; }); };
  var toggleInvSort = function (col) { setInvSort(function (p) { return p.col === col ? { col: col, dir: p.dir === "asc" ? "desc" : "asc" } : { col: col, dir: "desc" }; }); };
  var billArrow = function (c) { return billSort.col === c ? (billSort.dir === "asc" ? " ▲" : " ▼") : ""; };
  var invArrow = function (c) { return invSort.col === c ? (invSort.dir === "asc" ? " ▲" : " ▼") : ""; };

  var billAllSelected = sortedBills.length > 0 && sortedBills.every(function (b) { return billSelected[b.id]; });
  var invAllSelected = sortedInvoices.length > 0 && sortedInvoices.every(function (i) { return invSelected[i.id]; });
  var toggleAllBills = function () { var ns = {}; if (!billAllSelected) sortedBills.forEach(function (b) { ns[b.id] = true; }); setBillSelected(ns); };
  var toggleAllInvs = function () { var ns = {}; if (!invAllSelected) sortedInvoices.forEach(function (i) { ns[i.id] = true; }); setInvSelected(ns); };

  /* Excel export — independent per-panel buttons. Settlements is a
     read-only liquidation view; the rows already carry pre-computed
     _gross / _deductions / _paid / _season fields, so the Summary
     export just maps those. Detail expands each settled record into
     its product/fee/credit/transfer rows. */
  var settledBillsToExport = function () { var sel = sortedBills.filter(function (b) { return billSelected[b.id]; }); return sel.length > 0 ? sel : sortedBills; };
  var settledInvsToExport = function () { var sel = sortedInvoices.filter(function (i) { return invSelected[i.id]; }); return sel.length > 0 ? sel : sortedInvoices; };
  var buildSettleBillSummary = function () {
    return settledBillsToExport().map(function (b) {
      return {
        Lot: xs(b.lot),
        Date: xs(b.billDate),
        Shipper: xs(b.shipperName),
        "Invoice / Proforma": xs(b._invProf),
        "Purchase Amount": num2(b._gross),
        "Total Deductions": num2(b._deductions),
        "Amount Paid": num2(b._paid),
        Season: xs(b._season),
      };
    });
  };
  var buildSettleBillDetail = function () {
    var out = [];
    settledBillsToExport().forEach(function (b) {
      var meta = { Lot: xs(b.lot), Date: xs(b.billDate), Shipper: xs(b.shipperName), Season: xs(b._season) };
      (b.lines || []).forEach(function (l) {
        var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
        out.push({ ...meta, Type: "Product", Product: xs(l.productBase || l.productName), Description: xs(resolveLineDesc(l)), Cases: intOr(l.cases), Pallets: intOr(l.pallets), Rate: num2(l.reportedPrice), Amount: num2(amt) });
      });
      (b.fees || []).forEach(function (fe) {
        var fname = fe.feeType === "__custom__" ? (fe.feeCustom || "Custom Fee") : (fe.feeType || "Fee");
        out.push({ ...meta, Type: "Fee", Product: "", Description: xs(fname), Cases: "", Pallets: intOr(fe.qty), Rate: num2(fe.rate), Amount: num2(-Math.abs(parseFloat(fe.amount) || 0)) });
      });
      (b.credits || []).forEach(function (cr) {
        out.push({ ...meta, Type: "Credit", Product: "", Description: xs(cr.concept || ("Credit " + (cr.refLot || ""))), Cases: "", Pallets: "", Rate: "", Amount: num2(Math.abs(parseFloat(cr.amount) || 0)) });
      });
      (b.balanceTransfers || []).forEach(function (tr) {
        var amt = parseFloat(tr.amount) || 0;
        var signed = tr.type === "credit" ? amt : -amt;
        out.push({ ...meta, Type: "Transfer", Product: "", Description: (tr.type === "credit" ? "Credit from LOT " : "Debit to LOT ") + (tr.otherLot || "?"), Cases: "", Pallets: "", Rate: "", Amount: num2(signed) });
      });
    });
    return out;
  };
  var buildSettleInvSummary = function () {
    return settledInvsToExport().map(function (i) {
      return {
        "PU#": xs(i.puNumber),
        Date: xs(i.date),
        Client: xs(i.clientName),
        Lot: xs(i.lot),
        "Gross Sales": num2(i._gross),
        "Total Deductions": num2(i._deductions),
        "Amount Paid": num2(i._paid),
        Season: xs(i._season),
      };
    });
  };
  var buildSettleInvDetail = function () {
    var out = [];
    settledInvsToExport().forEach(function (i) {
      var meta = { "PU#": xs(i.puNumber), Date: xs(i.date), Client: xs(i.clientName), Lot: xs(i.lot), Season: xs(i._season) };
      (i.lines || []).forEach(function (l) {
        var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
        out.push({ ...meta, Type: "Product", Product: xs(l.productBase || l.productName), Description: xs(resolveLineDesc(l)), Cases: intOr(l.cases), Pallets: intOr(l.pallets), Rate: num2(l.salePrice), Amount: num2(amt) });
      });
      (i.fees || []).forEach(function (fe) {
        var fname = fe.feeType === "__custom__" ? (fe.feeCustom || "Custom Fee") : (fe.feeType || "Fee");
        out.push({ ...meta, Type: "Fee", Product: "", Description: xs(fname), Cases: "", Pallets: intOr(fe.qty), Rate: num2(fe.rate), Amount: num2(-Math.abs(parseFloat(fe.amount) || 0)) });
      });
      (i.credits || []).forEach(function (cr) {
        out.push({ ...meta, Type: "Credit", Product: "", Description: xs(cr.concept || ("Credit " + (cr.refPuNumber || ""))), Cases: "", Pallets: "", Rate: "", Amount: num2(Math.abs(parseFloat(cr.amount) || 0)) });
      });
      (i.balanceTransfers || []).forEach(function (tr) {
        var amt = parseFloat(tr.amount) || 0;
        var signed = tr.type === "credit" ? amt : -amt;
        out.push({ ...meta, Type: "Transfer", Product: "", Description: (tr.type === "credit" ? "Credit from PU# " : "Debit to PU# ") + (tr.otherPuNumber || "?"), Cases: "", Pallets: "", Rate: "", Amount: num2(signed) });
      });
    });
    return out;
  };

  var inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  var th = { padding: "9px 6px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", borderBottom: "2px solid var(--color-border)", position: "sticky", top: 0, background: "var(--color-bg-card-solid)", zIndex: 2 };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Local CSS so cross-panel highlight paints the WHOLE row reliably
          (tr + every td) — browser quirks around <tr> background under
          borderCollapse:collapse can otherwise hide it. */}
      <style>{"tr.settlement-highlight, tr.settlement-highlight > td { background-color: #FFF9E6 !important; }"}</style>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2>
        <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t.subtitle}</span>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
        <div style={{ padding: 14, borderRadius: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", borderLeft: "4px solid #059669" }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.totalCollected}</div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: "#059669", marginTop: 4 }}>{fmtMoney(totalCollected)}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", borderLeft: "4px solid #DC2626" }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.totalPaidShippers}</div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: "#DC2626", marginTop: 4 }}>{fmtMoney(totalPaidShippers)}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", borderLeft: "4px solid #1B3A4B" }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.realProfit}</div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: realProfit >= 0 ? "#1B3A4B" : "#DC2626", marginTop: 4 }}>{fmtMoney(realProfit)}</div>
        </div>
      </div>

      {/* Two-panel layout — flex 1 with min-height 0 so internal scrolling works */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, flex: 1, minHeight: 0 }}>
        {/* LEFT PANEL — Shipper Settlements */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, border: "1px solid var(--color-border)", borderRadius: 10, overflow: "hidden", background: "var(--color-bg-card-solid)" }}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-accent)" }}>{t.shipperPanel}</div>
              <ExportButton lang={lang} filenameBase="Settlements_Bills" selectedCount={billSelectCount} totalCount={sortedBills.length} buildSummary={buildSettleBillSummary} buildDetail={buildSettleBillDetail} />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input value={billSearch} onChange={function (e) { setBillSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, flex: 1, minWidth: 120 }} />
              <SearchSelect value={billShipper} onChange={setBillShipper} placeholder={t.allShippers} style={{ minWidth: 130 }} options={uniqueShippers.map(function (s) { return { value: s, label: s }; })} />
              <SearchSelect multi value={billSeasons} onChange={setBillSeasons} placeholder={seasonFilterPlaceholder(lang)} style={{ minWidth: 160 }} groups={buildSeasonFilterOptions(lang)} />
              <input type="date" value={billDateFrom} onChange={function (e) { setBillDateFrom(e.target.value); }} title={lang === "es" ? "Desde" : "From"} style={{ ...inp, minWidth: 120 }} />
              <input type="date" value={billDateTo} onChange={function (e) { setBillDateTo(e.target.value); }} title={lang === "es" ? "Hasta" : "To"} style={{ ...inp, minWidth: 120 }} />
              {(billDateFrom || billDateTo) && <button onClick={function () { setBillDateFrom(""); setBillDateTo(""); }} style={{ padding: "6px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>}
            </div>
            {billSelectCount > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, padding: "6px 8px", borderRadius: 6, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)" }}>
                <button onClick={printSelectedBills} style={{ padding: "5px 12px", borderRadius: 5, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{t.salesReportPdf}</button>
                <button onClick={function () { setBillSelected({}); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>Clear</button>
                <div style={{ flex: 1, textAlign: "right", fontSize: 11, fontWeight: 600, color: "var(--color-accent)" }}>{billSelectCount} {t.selected} <span style={{ color: "var(--color-text-muted)" }}>|</span> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(billSelectTotal)} USD</span></div>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                <th style={{ ...th, width: 22, cursor: "default" }}></th>
                <th style={{ ...th, width: 26, cursor: "default" }}><input type="checkbox" checked={billAllSelected} onChange={toggleAllBills} /></th>
                <th onClick={function () { toggleBillSort("lot"); }} style={{ ...th, textAlign: "center" }}>{t.lot}{billArrow("lot")}</th>
                <th onClick={function () { toggleBillSort("billDate"); }} style={th}>{t.date}{billArrow("billDate")}</th>
                <th onClick={function () { toggleBillSort("_invProf"); }} style={th}>{t.invoiceProforma}{billArrow("_invProf")}</th>
                <th onClick={function () { toggleBillSort("shipperName"); }} style={th}>{t.shipper}{billArrow("shipperName")}</th>
                <th onClick={function () { toggleBillSort("_gross"); }} style={{ ...th, textAlign: "right" }}>{t.purchaseAmount}{billArrow("_gross")}</th>
                <th onClick={function () { toggleBillSort("_deductions"); }} style={{ ...th, textAlign: "right" }}>{t.totalDeductions}{billArrow("_deductions")}</th>
                <th onClick={function () { toggleBillSort("_paid"); }} style={{ ...th, textAlign: "right" }}>{t.amountPaid}{billArrow("_paid")}</th>
                <th onClick={function () { toggleBillSort("_season"); }} style={th}>{t.season}{billArrow("_season")}</th>
              </tr></thead>
              <tbody>
                {sortedBills.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: "30px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.noResults}</td></tr>
                )}
                {sortedBills.map(function (b) {
                  var isExpanded = !!expandedBills[b.id];
                  var isHighlighted = !!(b.lot && highlightedBillLots.has(String(b.lot)));
                  /* Related invoices: any invoice whose lot matches. We compute
                     it inline only when expanded to avoid mass-iterating on
                     every render. Match both top-level inv.lot and per-line
                     lot for invoices that store the lot on lines only. */
                  var related = isExpanded
                    ? invoices.filter(function (inv) {
                        if (String(inv.lot) === String(b.lot)) return true;
                        return (inv.lines || []).some(function (l) { return String(l.lot) === String(b.lot); });
                      })
                    : [];
                  /* Selection tint (subtle green) lives in the inline style;
                     cross-panel highlight (yellow) is applied via the
                     .settlement-highlight class so it covers tr + every td
                     consistently across browsers. */
                  var rowBg = billSelected[b.id] ? "rgba(42,122,111,0.04)" : "transparent";
                  return (
                    <React.Fragment key={b.id}>
                      <tr ref={function (el) { if (el) billRowRefs.current[b.id] = el; else delete billRowRefs.current[b.id]; }} data-lot={String(b.lot || "")} className={isHighlighted ? "settlement-highlight" : undefined} onClick={function () { setViewingBill(b); }} style={{ cursor: "pointer", borderBottom: isExpanded ? "none" : "1px solid var(--color-border)", background: rowBg, transition: "background 0.2s" }}>
                        <td style={{ padding: "8px 4px", textAlign: "center", cursor: "pointer", color: "var(--color-text-muted)" }} onClick={function (e) { e.stopPropagation(); setExpandedBills(function (p) { var ns = { ...p }; if (ns[b.id]) delete ns[b.id]; else ns[b.id] = true; return ns; }); }} title={isExpanded ? "Collapse" : "Show invoices"}>
                          <span style={{ fontSize: 10, display: "inline-block", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>{"▶"}</span>
                        </td>
                        <td style={{ padding: "8px 6px", textAlign: "center" }} onClick={function (e) { e.stopPropagation(); }}><input type="checkbox" checked={!!billSelected[b.id]} onChange={function () { setBillSelected(function (p) { var ns = { ...p }; if (ns[b.id]) delete ns[b.id]; else ns[b.id] = true; return ns; }); }} /></td>
                        <td style={{ padding: "8px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", textAlign: "center" }}>{b.lot}</td>
                        <td style={{ padding: "8px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(b.billDate)}</td>
                        <td style={{ padding: "8px 6px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{b._invProf || "—"}</td>
                        <td style={{ padding: "8px 6px", fontSize: 12, fontWeight: 600 }}>{b.shipperName || "—"}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(b._gross)}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#DC2626" }}>({fmtMoney(b._deductions)})</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(b._paid)}</td>
                        <td style={{ padding: "8px 6px", fontSize: 11, color: "var(--color-text-muted)" }}>{b._season || "—"}</td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: "1px solid var(--color-border)", background: "rgba(42,122,111,0.03)" }}>
                          <td colSpan={10} style={{ padding: "8px 32px 12px" }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{lang === "es" ? "Facturado a:" : "Invoiced to:"}</div>
                            {related.length === 0 ? (
                              <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noRelated}</div>
                            ) : (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                <tbody>{related.map(function (inv) {
                                  var amt = calcInvoiceTotal(inv).total;
                                  return (
                                    <tr key={inv.id} onClick={function (e) { e.stopPropagation(); setViewingInvoice(inv); }} style={{ cursor: "pointer", borderBottom: "1px solid rgba(0,0,0,0.04)" }}
                                        onMouseEnter={function (e) { e.currentTarget.style.background = "rgba(198,93,0,0.06)"; }}
                                        onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                                      <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00", width: "20%" }}>PU# {inv.puNumber} →</td>
                                      <td style={{ padding: "5px 6px", color: "var(--color-text)" }}>{inv.clientName || "—"}</td>
                                      <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, width: "20%" }}>{fmtMoney(amt)}</td>
                                    </tr>
                                  );
                                })}</tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT PANEL — Client Settlements */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, border: "1px solid var(--color-border)", borderRadius: 10, overflow: "hidden", background: "var(--color-bg-card-solid)" }}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#c65d00" }}>{t.clientPanel}</div>
              <ExportButton lang={lang} filenameBase="Settlements_Invoices" selectedCount={invSelectCount} totalCount={sortedInvoices.length} buildSummary={buildSettleInvSummary} buildDetail={buildSettleInvDetail} />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input value={invSearch} onChange={function (e) { setInvSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, flex: 1, minWidth: 120 }} />
              <SearchSelect value={invClient} onChange={setInvClient} placeholder={t.allClients} style={{ minWidth: 130 }} options={uniqueClients.map(function (c) { return { value: c, label: c }; })} />
              <SearchSelect multi value={invSeasons} onChange={setInvSeasons} placeholder={seasonFilterPlaceholder(lang)} style={{ minWidth: 160 }} groups={buildSeasonFilterOptions(lang)} />
              <input type="date" value={invDateFrom} onChange={function (e) { setInvDateFrom(e.target.value); }} title={lang === "es" ? "Desde" : "From"} style={{ ...inp, minWidth: 120 }} />
              <input type="date" value={invDateTo} onChange={function (e) { setInvDateTo(e.target.value); }} title={lang === "es" ? "Hasta" : "To"} style={{ ...inp, minWidth: 120 }} />
              {(invDateFrom || invDateTo) && <button onClick={function () { setInvDateFrom(""); setInvDateTo(""); }} style={{ padding: "6px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>}
            </div>
            {invSelectCount > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, padding: "6px 8px", borderRadius: 6, background: "rgba(198,93,0,0.06)", border: "1px solid rgba(198,93,0,0.15)" }}>
                <button onClick={printSelectedInvoices} style={{ padding: "5px 12px", borderRadius: 5, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{t.invoicePdf}</button>
                <button onClick={function () { setInvSelected({}); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>Clear</button>
                <div style={{ flex: 1, textAlign: "right", fontSize: 11, fontWeight: 600, color: "#c65d00" }}>{invSelectCount} {t.selected} <span style={{ color: "var(--color-text-muted)" }}>|</span> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(invSelectTotal)} USD</span></div>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                <th style={{ ...th, width: 22, cursor: "default" }}></th>
                <th style={{ ...th, width: 26, cursor: "default" }}><input type="checkbox" checked={invAllSelected} onChange={toggleAllInvs} /></th>
                <th onClick={function () { toggleInvSort("puNumber"); }} style={{ ...th, textAlign: "center" }}>{t.invoiceNum}{invArrow("puNumber")}</th>
                <th onClick={function () { toggleInvSort("date"); }} style={th}>{t.date}{invArrow("date")}</th>
                <th onClick={function () { toggleInvSort("_customerPO"); }} style={th}>{t.customerPO}{invArrow("_customerPO")}</th>
                <th onClick={function () { toggleInvSort("clientName"); }} style={th}>{t.client}{invArrow("clientName")}</th>
                <th onClick={function () { toggleInvSort("_amount"); }} style={{ ...th, textAlign: "right" }}>{t.salesAmount}{invArrow("_amount")}</th>
                <th onClick={function () { toggleInvSort("_deductions"); }} style={{ ...th, textAlign: "right" }}>{t.totalDeductions}{invArrow("_deductions")}</th>
                <th onClick={function () { toggleInvSort("_paid"); }} style={{ ...th, textAlign: "right" }}>{t.amountCollected}{invArrow("_paid")}</th>
                <th onClick={function () { toggleInvSort("_season"); }} style={th}>{t.season}{invArrow("_season")}</th>
              </tr></thead>
              <tbody>
                {sortedInvoices.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: "30px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.noResults}</td></tr>
                )}
                {sortedInvoices.map(function (inv) {
                  var isExpanded = !!expandedInvoices[inv.id];
                  var isHighlighted = invoiceMatchesHighlight(inv);
                  /* Related bills/shipments: bills whose lot matches the
                     invoice's lot (top-level or any line). */
                  var related = isExpanded
                    ? bills.filter(function (b) {
                        if (String(b.lot) === String(inv.lot)) return true;
                        return (inv.lines || []).some(function (l) { return String(l.lot) === String(b.lot); });
                      })
                    : [];
                  var rowBg = invSelected[inv.id] ? "rgba(198,93,0,0.04)" : "transparent";
                  return (
                    <React.Fragment key={inv.id}>
                      <tr ref={function (el) { if (el) invRowRefs.current[inv.id] = el; else delete invRowRefs.current[inv.id]; }} data-lot={String(inv.lot || "")} className={isHighlighted ? "settlement-highlight" : undefined} onClick={function () { setViewingInvoice(inv); }} style={{ cursor: "pointer", borderBottom: isExpanded ? "none" : "1px solid var(--color-border)", background: rowBg, transition: "background 0.2s" }}>
                        <td style={{ padding: "8px 4px", textAlign: "center", cursor: "pointer", color: "var(--color-text-muted)" }} onClick={function (e) { e.stopPropagation(); setExpandedInvoices(function (p) { var ns = { ...p }; if (ns[inv.id]) delete ns[inv.id]; else ns[inv.id] = true; return ns; }); }} title={isExpanded ? "Collapse" : "Show shipments"}>
                          <span style={{ fontSize: 10, display: "inline-block", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>{"▶"}</span>
                        </td>
                        <td style={{ padding: "8px 6px", textAlign: "center" }} onClick={function (e) { e.stopPropagation(); }}><input type="checkbox" checked={!!invSelected[inv.id]} onChange={function () { setInvSelected(function (p) { var ns = { ...p }; if (ns[inv.id]) delete ns[inv.id]; else ns[inv.id] = true; return ns; }); }} /></td>
                        <td style={{ padding: "8px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00", textAlign: "center" }}>{inv.puNumber}</td>
                        <td style={{ padding: "8px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(inv.date)}</td>
                        <td style={{ padding: "8px 6px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{inv._customerPO || "—"}</td>
                        <td style={{ padding: "8px 6px", fontSize: 12, fontWeight: 600 }}>{inv.clientName || "—"}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(inv._amount)}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#DC2626" }}>{inv._deductions > 0 ? "(" + fmtMoney(inv._deductions) + ")" : "—"}</td>
                        <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(inv._paid)}</td>
                        <td style={{ padding: "8px 6px", fontSize: 11, color: "var(--color-text-muted)" }}>{inv._season || "—"}</td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: "1px solid var(--color-border)", background: "rgba(198,93,0,0.03)" }}>
                          <td colSpan={10} style={{ padding: "8px 32px 12px" }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{lang === "es" ? "Desde embarques:" : "From shipments:"}</div>
                            {related.length === 0 ? (
                              <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noRelated}</div>
                            ) : (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                <tbody>{related.map(function (b) {
                                  var sub = calcBillSubtotal(b).subtotal;
                                  return (
                                    <tr key={b.id} onClick={function (e) { e.stopPropagation(); setViewingBill(b); }} style={{ cursor: "pointer", borderBottom: "1px solid rgba(0,0,0,0.04)" }}
                                        onMouseEnter={function (e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                                        onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                                      <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", width: "20%" }}>LOT {b.lot} →</td>
                                      <td style={{ padding: "5px 6px", color: "var(--color-text)" }}>{b.shipperName || "—"}</td>
                                      <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, width: "20%" }}>{fmtMoney(sub)}</td>
                                    </tr>
                                  );
                                })}</tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {viewingBill && <BillDetailReadOnly bill={viewingBill} t={t} lang={lang}
        onClose={function () { setViewingBill(null); }}
        onOpenInvoice={function (inv) { setViewingBill(null); setViewingInvoice(inv); }} />}
      {viewingInvoice && <InvoiceDetailReadOnly invoice={viewingInvoice} t={t} lang={lang}
        onClose={function () { setViewingInvoice(null); }}
        onOpenBill={function (b) { setViewingInvoice(null); setViewingBill(b); }} />}
      {toast && createPortal(
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }} onClick={function () { setToast(""); }}>{toast}</div>,
        document.body
      )}

      {/* unused — silence lint warning for LOGO_B64 import */}
      <span style={{ display: "none" }}>{LOGO_B64 ? "" : ""}</span>
    </div>
  );
}
